import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  styled,
  Typography
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useLoading } from 'contexts/loading';
import { StyledParams } from 'types/common';
import {
  Nav_Activities,
  Nav_Client,
  Nav_Document,
  Nav_Reports,
  Nav_Floorplan,
  Nav_Setting,
  Nav_User,
  Nav_Maintenance,
  Nav_Validation
} from 'assets/index';
import { StorageServices } from 'services/storage';
import { useSystemInfo } from 'contexts/systemInfo';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { getInfoUser } from 'services/auth';
import { checkActive } from 'utils/checkActivePage';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  getRecentNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  UserNotification
} from 'services/notification';
import { getNotificationNavigationPath } from 'utils/notificationNavigation';

type SideMenu = {
  label: string;
  link: string;
  active: boolean;
  icon?: ReactNode;
  items?: SideMenu[];
};

type LayoutProps = {
  children: ReactNode;
};

const storage = new StorageServices();

export const RESTRICTIONS = {
  ACTIVITIES: 'ACTIVITIES',
  VALIDATION: 'VALIDATION',
  DOCUMENTATION: 'DOCUMENTATION',
  REPORTS: 'REPORTS',
  ISSUES: 'ISSUES',
  MAINTENANCE: 'MAINTENANCE'
};

export const LayoutAdmin = ({ children }: LayoutProps) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { systemInfo } = useSystemInfo();
  const { loading: callbackLoading } = useLoading();
  const { width } = useWindowDimensions();
  const isMobile = width < 900;

  const { auth, setAuth } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifications, setRecentNotifications] = useState<UserNotification[]>([]);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);

  const MENU: SideMenu[] = useMemo(
    () => [
      {
        label: 'Activities',
        link: '/activities',
        icon: <Nav_Activities />,
        active: checkActive(auth, RESTRICTIONS.ACTIVITIES)
      },
      {
        label: 'Clients',
        icon: <Nav_Client />,
        link: '/clients',
        active: true
      },
      {
        label: 'Inspection',
        icon: <Nav_Floorplan />,
        link: '/inspection',
        active: process.env.REACT_APP_FLOORPLAN_VISIBLE === 'true'
      },
      {
        label: 'Maitainence',
        icon: <Nav_Maintenance />,
        link: '/maintenance',
        active: checkActive(auth, RESTRICTIONS.MAINTENANCE)
      },
      {
        label: 'Issues',
        icon: <Nav_Reports />,
        link: '/issues',
        active: checkActive(auth, RESTRICTIONS.ISSUES)
      },
      {
        label: 'Reports',
        icon: <Nav_Reports />,
        link: '/reports',
        active: checkActive(auth, RESTRICTIONS.REPORTS)
      },
      {
        label: 'Validation',
        icon: <Nav_Validation />,
        link: '/validation',
        active: checkActive(auth, RESTRICTIONS.VALIDATION)
      },
      {
        label: 'Documentation',
        icon: <Nav_Document />,
        link: '/documentation',
        active: checkActive(auth, RESTRICTIONS.DOCUMENTATION)
      },
      {
        label: 'Background Jobs',
        icon: <Nav_Document />,
        link: '/background-jobs',
        active: checkActive(auth, RESTRICTIONS.REPORTS)
      },
      // {
      //   label: 'Settings',
      //   icon: <Nav_Setting />,
      //   link: '/settings',
      //   active: true
      // },
      {
        label: 'Settings',
        icon: <Nav_Setting />,
        link: '/settings-hub',
        active: true
      },
      // {
      //   label: 'Notification Settings',
      //   icon: <Nav_Setting />,
      //   link: '/notification-settings',
      //   active: auth?.role === Role.SUPER_ADMIN || auth?.role === Role.ADMIN
      // },
      {
        label: 'Options',
        icon: <Nav_Setting />,
        link: '/options',
        active: auth?.role === Role.SUPER_ADMIN || auth?.role === Role.ADMIN
      },
      {
        label: 'Users',
        icon: <Nav_User />,
        link: '/users',
        active: auth?.role === Role.SUPER_ADMIN || auth?.role === Role.ADMIN
      }
    ],
    [auth]
  );

  const checkPermission = useCallback(() => {
    if (pathname.startsWith('/clients')) return;
    if (pathname.startsWith('/setting')) return;
    if (pathname.startsWith('/notifications')) return;
    if (pathname.startsWith('/reports') && checkActive(auth, RESTRICTIONS.REPORTS)) return;
    if (pathname.startsWith('/issues') && checkActive(auth, RESTRICTIONS.ISSUES)) return;
    if (pathname.startsWith('/validation') && checkActive(auth, RESTRICTIONS.VALIDATION)) return;
    if (pathname.startsWith('/activities') && checkActive(auth, RESTRICTIONS.ACTIVITIES)) return;
    if (pathname.startsWith('/documentation') && checkActive(auth, RESTRICTIONS.DOCUMENTATION))
      return;
    if (pathname.startsWith('/background-jobs') && checkActive(auth, RESTRICTIONS.REPORTS)) return;
    if (pathname.startsWith('/maintenance') && checkActive(auth, RESTRICTIONS.MAINTENANCE)) return;
  }, [auth, pathname]);

  const getUser = useCallback(async () => {
    const data = await getInfoUser();
    setAuth(data.user);
  }, [setAuth]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    if (auth) {
      checkPermission();
    }
  }, [auth, checkPermission]);

  const loadNotificationData = useCallback(async () => {
    if (!auth) return;
    try {
      const [countRes, recentRes] = await Promise.all([
        getUnreadNotificationCount(),
        getRecentNotifications(15)
      ]);
      setUnreadCount(countRes.unreadCount || 0);
      setRecentNotifications(recentRes.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications', error);
    }
  }, [auth]);

  useEffect(() => {
    loadNotificationData();
    const interval = setInterval(loadNotificationData, 30000);
    const onRefresh = () => {
      loadNotificationData();
    };
    window.addEventListener('notifications:refresh', onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications:refresh', onRefresh);
    };
  }, [loadNotificationData]);

  const handleLogout = useCallback(() => {
    storage.removeAccessToken();
    storage.removeRefreshToken();
    navigate('/login');
  }, [navigate]);

  return (
    <>
      {/* <DisclaimerAlert message="You are using an early-release version of this platform. We are actively working on improvements and appreciate your feedback and patience as we continue refining the experience." /> */}
      <Stack
        minHeight={'100vh'}
        flexDirection={isMobile ? 'column' : 'row'}
        width={width - 20}
        position={'relative'}>
        {callbackLoading && <LinearLoading color="success" />}
        <WrapperNavbar py={2.5} px={3} justifyContent={'space-between'}>
          <Stack style={{ width: '100%' }}>
            <Button sx={{ width: '100%' }}>
              <Stack
                width={'100%'}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}>
                <img
                  style={{ cursor: 'pointer' }}
                  alt={'img'}
                  width={150}
                  src={'/images/logo-text-1.png'}
                  onClick={() => navigate('/admin')}
                />
                {systemInfo?.logoUrl && (
                  <img
                    style={{ cursor: 'pointer' }}
                    alt={'img'}
                    width={32}
                    src={systemInfo?.logoUrl}
                    onClick={() => navigate('/admin')}
                  />
                )}
              </Stack>
            </Button>
            <Wrapper width={'100%'} gap={2}>
              {MENU.map((item, index) => {
                if (!item.active) return;
                return (
                  <NavLink to={item.link} key={index}>
                    <WrapperItem className={'wrapper-item'} gap={'14px'}>
                      <Stack className={'wrapper-image'}>{item.icon}</Stack>
                      <TextStyled className={'text-label'}>{item.label}</TextStyled>
                    </WrapperItem>
                  </NavLink>
                );
              })}
            </Wrapper>
          </Stack>
          <Button sx={{ width: '100%' }} onClick={handleLogout}>
            <Stack
              width={'100%'}
              gap={3}
              p={1.5}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'flex-start'}>
              <img
                style={{ cursor: 'pointer' }}
                alt={'img'}
                width={20}
                src={'/icons/icon_logout.svg'}
              />
              <TextStyled className={'text-label'}>Log Out</TextStyled>
            </Stack>
          </Button>
        </WrapperNavbar>
        <Stack
          width={width - 261}
          sx={{ background: '#fff' }}
          alignItems={'center'}
          ml={isMobile ? 'unset' : '253px'}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: 8,
              right: 0,
              zIndex: 20
            }}>
            <Badge
              color="error"
              badgeContent={unreadCount > 99 ? '99+' : unreadCount}
              overlap="circular"
              sx={{ '& .MuiBadge-badge': { fontSize: '10px' } }}>
              <IconButton
                onClick={(event) => setNotificationAnchorEl(event.currentTarget)}
                sx={{
                  color: '#fff',
                  background: '#226AAA',
                  border: '1px solid rgba(255,255,255,0.3)',
                  '&:hover': { background: '#1b5f98' }
                }}
                size="small">
                <NotificationsIcon />
              </IconButton>
            </Badge>
          </Stack>
          {children}
        </Stack>
        <Popover
          open={Boolean(notificationAnchorEl)}
          anchorEl={notificationAnchorEl}
          onClose={() => setNotificationAnchorEl(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          slotProps={{ paper: { sx: { ml: 1, mt: 4 } } }}>
          <Box sx={{ width: 360, maxHeight: 460, overflow: 'auto' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              py={1.5}>
              <Typography fontWeight={800}>Recent Notifications</Typography>
              <Typography
                sx={{ cursor: 'pointer', color: '#226AAA', fontSize: 12 }}
                onClick={() => {
                  setNotificationAnchorEl(null);
                  navigate('/notifications');
                }}>
                View all
              </Typography>
            </Stack>
            <Divider />
            <List disablePadding>
              {recentNotifications.map((item) => (
                <ListItemButton
                  key={item.id}
                  sx={{ alignItems: 'flex-start', background: item.isRead ? '#fff' : '#f3f9ff' }}
                  onClick={async () => {
                    if (!item.isRead) {
                      await markNotificationRead(item.id);
                    }
                    setNotificationAnchorEl(null);
                    navigate(getNotificationNavigationPath(item));
                    loadNotificationData();
                  }}>
                  <ListItemText
                    primary={
                      <Typography fontSize={13} fontWeight={700}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography fontSize={12} color="text.secondary">
                        {item.body}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Popover>
      </Stack>
    </>
  );
};

const Wrapper = styled(Stack)(({ theme }: StyledParams) => ({
  width: '100%',
  '& .active': {
    '& .wrapper-item': {
      background: theme.colors.white,
      '& svg': {
        color: theme.colors.base
      },
      '.text-label': {
        color: theme.colors.base,
        fontWeight: '800'
      }
    }
  },
  '& svg': {
    color: theme.colors.white
  }
}));

const LinearLoading = styled(LinearProgress)(() => ({
  position: 'fixed',
  width: '100vw',
  zIndex: 1000
}));
const WrapperNavbar = styled(Stack)(({ theme }: StyledParams) => ({
  width: '261px',
  background: theme.colors.base,
  alignItems: 'center',
  height: '100vh',
  zIndex: 5,
  position: 'fixed',
  overflow: 'auto'
}));

const WrapperItem = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  borderRadius: '12px',
  gap: '19px',
  padding: '12px'
}));
const TextStyled = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: '500',
  color: '#fff'
}));
